import {
  runStatusOf,
  type ProgramNewsRun,
  type ProgramNewsRunCounters,
  type ProgramNewsRunType,
} from "@/services/adminProgramNews";
import { formatUsd } from "./formatters";

export interface HeadlineCounter {
  /** Short display text, e.g. "18 items" or "$3.70/mo". */
  text: string;
  /** Extra word(s) for the toast, e.g. "projected"; shown as a title on screen. */
  note?: string;
}

function count(
  counters: ProgramNewsRunCounters,
  key: keyof ProgramNewsRunCounters,
  unit: string
): HeadlineCounter | null {
  const value = counters[key];
  if (typeof value !== "number") return null;
  return { text: `${value} ${unit}` };
}

/** The handful of counters an operator glances at per run. Missing counters are skipped. */
export function headlineCounters(
  type: ProgramNewsRunType,
  counters: ProgramNewsRunCounters | null | undefined
): HeadlineCounter[] {
  const c = counters ?? {};
  const entries: Array<HeadlineCounter | null> =
    type === "collect"
      ? [
          count(c, "sources", "sources"),
          count(c, "ok", "ok"),
          count(c, "errors", "errors"),
          count(c, "newItems", "new"),
          count(c, "zeroItemWarnings", "zero-item warnings"),
        ]
      : [
          count(c, "items", "items"),
          count(c, "alerts", "alerts"),
          count(c, "digests", "digests"),
          count(c, "ignores", "ignores"),
          typeof c.costUsd === "number" ? { text: formatUsd(c.costUsd) } : null,
          typeof c.projectedMonthlyUsd === "number"
            ? { text: `${formatUsd(c.projectedMonthlyUsd)}/mo`, note: "projected" }
            : null,
        ];
  return entries.filter((entry): entry is HeadlineCounter => entry !== null);
}

/** One-line summary for the success toast. */
export function describeRunCounters(
  type: ProgramNewsRunType,
  counters: ProgramNewsRunCounters | null | undefined
): string {
  const parts = headlineCounters(type, counters).map(({ text, note }) =>
    note ? `${text} ${note}` : text
  );
  return parts.length ? parts.join(" · ") : "Run finished (no counters reported)";
}

/** The most recent finished classify run, if any (a running one has no cost yet). Runs arrive newest first. */
export function latestClassifyRun(runs: ProgramNewsRun[]): ProgramNewsRun | null {
  return runs.find(run => run.type === "classify" && runStatusOf(run) !== "running") ?? null;
}
